import { useState, useRef, useEffect } from 'react';
import { FilterItemCheckBox } from './UiItems';
import { useSearchList } from '@/hooks/use-search-list';
import { debounce } from 'lodash-es';
import { SearchMd } from '@kula-ai/icons-core';
import {
  Box,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  Flex,
  Icon,
} from '@chakra-ui/react';
import { FilterWrapper } from './FilterWrapper';
import { SecondaryFilterWrapper } from './SecondaryFilterWrapper';
import { KText } from '@/ui-library/components/text';

const FilterList = (props) => {
  const {
    isListLoading = false,
    showAvatar = false,
    valueKey = 'name',
    idKey = 'id',
    handleFilterItemClick = () => {},
    selected = {},
    placeholder,
    label = 'user',
    onSearch = () => {},
    userInput,
    filteredList,
    CustomRenderList,
    activeFilters,
    customElement = null,
    additionalItemProps = {},
  } = props;
  const listContainerRef = useRef(null);

  const selectedItems =
    Array.isArray(activeFilters) && activeFilters.length > 0
      ? filteredList?.filter((item) =>
          activeFilters.some((filter) => filter?.[idKey] === item?.[idKey])
        )
      : [];

  const remainingItems =
    Array.isArray(activeFilters) && activeFilters.length > 0
      ? filteredList?.filter(
          (item) =>
            !activeFilters.some((filter) => filter?.[idKey] === item?.[idKey])
        )
      : filteredList || [];

  const list = [...selectedItems, ...remainingItems];

  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, [filteredList, activeFilters]);

  return (
    <>
      <Box px="8px" mb="8px">
        <InputGroup alignItems="center">
          <InputLeftElement
            pointerEvents="none"
            children={
              <Icon as={SearchMd} color="secondary-800" width="18" h="100%" />
            }
          />
          <Input
            placeholder={placeholder || 'Search'}
            onChange={onSearch}
            ref={userInput}
            borderColor="secondary-200"
            _placeholder={{ color: 'secondary-500' }}
            _hover={{
              borderColor: 'secondary-200',
              boxShadow: 'none !important',
            }}
            _focus={{
              border: 'none',
              boxShadow: 'none !important',
              borderColor: 'secondary-200 !important',
            }}
          />
        </InputGroup>
      </Box>

      {customElement && (
        <Box px="8px" mb="8px">
          {customElement}
        </Box>
      )}

      <Box
        ref={listContainerRef}
        minHeight="45px"
        maxHeight="250px"
        overflow="auto"
        overflowX="hidden"
        wordBreak="break-word"
      >
        {isListLoading ? (
          <Flex minH="100px" alignItems="center" justifyContent="center">
            <Spinner color="primary-800" />
          </Flex>
        ) : CustomRenderList ? (
          <CustomRenderList
            selected={selected}
            onChange={handleFilterItemClick}
            {...props}
          />
        ) : (
          list?.map?.((item, index) => {
            const { avatar } = item;
            const label = item?.[valueKey];
            const id = item?.[idKey];
            const rightElementItem = additionalItemProps?.rightElement?.(item);
            return (
              <FilterItemCheckBox
                key={`${id}-${index}`}
                showAvatar={showAvatar}
                isSelected={selected?.has(id)}
                onSelect={() => handleFilterItemClick(item)}
                label={label}
                avatarSrc={avatar}
                avatarName={label}
                rightElement={rightElementItem}
                maxLabelWidth={
                  additionalItemProps.maxLabelWidth?.(item) || '230px'
                }
                item={item}
              />
            );
          })
        )}
        {filteredList?.length === 0 && (
          <Box textAlign="center" mt="12px">
            <KText
              fontSize="sm"
              color="secondary-500"
            >{`No matching ${label} found.`}</KText>
          </Box>
        )}
      </Box>
    </>
  );
};

export const FilterWithLocalSearch = (props) => {
  const {
    activeFilters = [],
    list = [],
    valueKey = 'name',
    idKey = 'id',
    type = 'popover',
    isMultiple = true,
    filterActions = () => {},
    modalActions = {},
    filterConfig = {},
    onAction = () => {},
    customElement = null,
    additionalItemProps = {},
    filteredList: customFilteredList = null,
  } = props;
  const { id: filterId } = filterConfig;

  const [selected, setSelected] = useState(new Set());
  const userInput = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const searchFilteredList = useSearchList({
    list,
    searchTerm: searchTerm,
    key: valueKey,
  });

  const searchCustomFilteredList = useSearchList({
    list: customFilteredList,
    searchTerm: searchTerm,
    key: valueKey,
  });

  const filteredList = searchCustomFilteredList || searchFilteredList;

  useEffect(() => {
    if (activeFilters?.length)
      setSelected(new Set(activeFilters?.map((i) => i?.[idKey])));
  }, [activeFilters, idKey]);

  const onSearch = debounce(() => {
    setSearchTerm(userInput?.current?.value);
  }, 200);

  const clearSearchTerm = () => {
    userInput.current.value = '';
    setSearchTerm('');
  };

  const handleFilterItemClick = (item) => {
    const id = item?.[idKey];
    setSelected((prev) => {
      if (prev.has(id)) {
        prev.delete(id);
        return new Set(prev);
      }
      if (!isMultiple) {
        prev.clear();
      }
      prev.add(id);
      return new Set(prev);
    });

    if (!isMultiple && selected?.size > 0) {
      filterActions({
        type: 'apply',
        value: list?.filter((item) => selected.has(item?.[idKey])),
        key: filterId,
      });

      if (type === 'secondary') {
        onAction();
      } else {
        modalActions?.onClose();
      }
    }
  };

  if (type === 'onlyList') {
    const { filteredList: _, ...otherProps } = props;
    return (
      <FilterList
        handleFilterItemClick={handleFilterItemClick}
        selected={selected}
        filteredList={filteredList}
        searchTerm={searchTerm}
        onSearch={onSearch}
        userInput={userInput}
        customElement={customElement}
        additionalItemProps={additionalItemProps}
        {...otherProps}
      />
    );
  }

  if (type === 'secondary') {
    const { filteredList: _, ...otherProps } = props;
    return (
      <SecondaryFilterWrapper
        {...otherProps}
        selected={list
          ?.filter((item) => selected.has(item?.[idKey]))
          ?.map((item) => item)}
        clearSearchTerm={clearSearchTerm}
        currentlySelected={[...selected]}
      >
        <FilterList
          handleFilterItemClick={handleFilterItemClick}
          selected={selected}
          filteredList={filteredList}
          searchTerm={searchTerm}
          onSearch={onSearch}
          userInput={userInput}
          customElement={customElement}
          additionalItemProps={additionalItemProps}
          {...otherProps}
        />
      </SecondaryFilterWrapper>
    );
  }

  const { filteredList: _, ...otherProps } = props;
  return (
    <FilterWrapper
      {...otherProps}
      selected={list
        ?.filter((item) => selected.has(item?.[idKey]))
        ?.map((item) => item)}
      clearSearchTerm={clearSearchTerm}
      currentlySelected={[...selected]}
    >
      <FilterList
        handleFilterItemClick={handleFilterItemClick}
        selected={selected}
        filteredList={filteredList}
        searchTerm={searchTerm}
        onSearch={onSearch}
        userInput={userInput}
        customElement={customElement}
        additionalItemProps={additionalItemProps}
        {...otherProps}
      />
    </FilterWrapper>
  );
};
